<script setup lang="ts">
import { ref, watch } from 'vue'
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'
import ProgressSpinner from 'primevue/progressspinner'
import LoadingSkeletonRow from '@/components/LoadingSkeletonRow.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import type { ActionPermissions } from '@/components/limit_action_copy'
import WikiText from '@/components/wikitext/WikiText.vue'
import type { WikiTextStrategy } from '@/components/wikitext/wiki_text_types'
import ResourceCard from '@/resources/ResourceCard.vue'
import type { Resource } from '@/resources/resource_types'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import Objective from './Objective.vue'
import { objectiveContentTitle, type AnchorObjective, type ObjectiveEdit } from './anchor_objective_types'
import type { ContentAccessInfo } from './content_access_info'
import { OBJECTIVE_EVENT_NAMES } from './event_names'
import { levelSectionHeadingClass, levelTabClass, type ObjectiveLevel } from './objective_levels'
import { LOADING_ROW_COUNT, LOADING_ROW_WIDTHS, objectiveEdit } from './objective_list'

type LevelLists<T> = Partial<Record<ObjectiveLevel, T[]>>

const ASSESSMENT_TYPE = 'Assessment'
const FIRST_TAB = '0'
const TAB_CLASSES =
  'flex-1 rounded-t-lg px-4 py-2.5 text-center text-text-xl font-regular data-[state=active]:font-[600] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2'
const SECTION_HEADING_CLASSES = 'mb-0 mt-2.5 border-t pb-0 pt-1 text-text-xl font-regular uppercase first:mt-10 first:pt-2'

const props = withDefaults(
  defineProps<{
    objectives: AnchorObjective[]
    objectiveTexts: string[]
    items?: LevelLists<string>
    pageUuid?: string
    accessInfo: ContentAccessInfo
    printPermissions: ActionPermissions
    isEditable?: boolean
    isReadOnly?: boolean
    readOnlyHint?: string
    isFetching?: boolean
    strategies?: Record<string, WikiTextStrategy>
    objectiveResourceUuids?: LevelLists<string>
    resourcesByLevel?: LevelLists<Resource>
    isFetchingResources?: boolean
    levelsWithGeneratedAssessments?: ObjectiveLevel[]
  }>(),
  {
    items: () => ({}),
    pageUuid: '',
    isEditable: false,
    isReadOnly: false,
    readOnlyHint: undefined,
    isFetching: false,
    strategies: () => ({}),
    objectiveResourceUuids: () => ({}),
    resourcesByLevel: () => ({}),
    isFetchingResources: false,
    levelsWithGeneratedAssessments: () => []
  }
)

const emit = defineEmits<{
  'update:objective': [edit: ObjectiveEdit]
}>()

// Generated assessments exist only on saved pages, which render their tiles here; the page
// also lists the levels it fills, since a slot cannot report whether it renders anything.
defineSlots<{
  generatedAssessments?(props: { objective: AnchorObjective, index: number }): unknown
}>()

const analytics = getAnalyticsTracker()

const activeTab = ref(FIRST_TAB)

watch(() => props.objectives, () => {
  activeTab.value = FIRST_TAB
})

function tabValue(index: number): string {
  return String(index)
}

// reka-ui reports a press on the tab that is already open too; only a switch counts.
function selectTab(tab: string | number) {
  const selectedTab = String(tab)
  if (selectedTab === activeTab.value) return
  activeTab.value = selectedTab
  const objective = props.objectives[Number(tab)]
  if (!objective) return
  analytics.sendEvent(
    OBJECTIVE_EVENT_NAMES.CLICKED_OBJECTIVE_TAB,
    { obj_title: objective.title, level: objective.level },
    { cu: props.pageUuid }
  )
}

function levelResources(level: ObjectiveLevel): Resource[] {
  return props.resourcesByLevel[level] ?? []
}

function curatedAssessments(level: ObjectiveLevel): Resource[] {
  return levelResources(level).filter((resource) => resource.type === ASSESSMENT_TYPE)
}

function readyToUseResources(level: ObjectiveLevel): Resource[] {
  return levelResources(level).filter((resource) => resource.type !== ASSESSMENT_TYPE)
}

function hasGeneratedAssessments(level: ObjectiveLevel): boolean {
  return props.levelsWithGeneratedAssessments.includes(level)
}

// Opens on the curated uuids rather than the fetched resources, so the loading row shows
// while they arrive.
function hasResourceSection(level: ObjectiveLevel): boolean {
  const hasCuratedResources = Boolean(props.objectiveResourceUuids[level]?.length)
  return hasCuratedResources || hasGeneratedAssessments(level)
}

function hasAssessmentsSection(level: ObjectiveLevel): boolean {
  return curatedAssessments(level).length > 0 || hasGeneratedAssessments(level)
}

function isEditorShown(index: number): boolean {
  return props.isEditable && Boolean(props.objectiveTexts[index])
}

function isReadOnlyTextShown(objective: AnchorObjective): boolean {
  return !props.isEditable && Boolean(objective.text)
}

function displayedText(objective: AnchorObjective, index: number): string {
  return props.objectiveTexts[index] || objective.text
}

function editObjective(index: number, text: string) {
  emit('update:objective', objectiveEdit(props.objectives, index, text, false))
}
</script>

<template>
  <div v-if="isFetching" data-testid="anchor-page-objectives-loading">
    <LoadingSkeletonRow v-for="row in LOADING_ROW_COUNT" :key="row" :possible-widths="LOADING_ROW_WIDTHS" />
  </div>
  <TabsRoot v-else :model-value="activeTab" activation-mode="manual" data-testid="anchor-page-objectives" @update:model-value="selectTab">
    <TabsList aria-label="Quarterly objectives" class="flex gap-0.5 max-md:flex-col max-md:gap-1">
      <TabsTrigger
        v-for="(objective, index) in objectives"
        :key="index"
        :value="tabValue(index)"
        :class="[TAB_CLASSES, levelTabClass(objective.level)]"
        :data-testid="`objective-tab-${objective.level}`"
      >
        {{ objective.level }} Quarter
      </TabsTrigger>
    </TabsList>
    <!-- Every quarter stays rendered, hidden, so its objective and resources are in the page for search engines -->
    <TabsContent
      v-for="(objective, index) in objectives"
      :key="index"
      :value="tabValue(index)"
      force-mount
      class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 data-[state=inactive]:hidden"
    >
      <Objective
        :level="objective.level"
        :title="objective.title"
        :text="objectiveTexts[index]"
        :items="items[objective.level]"
        :access-info="accessInfo"
        :print-permissions="printPermissions"
        :page-uuid="pageUuid"
      >
        <template #body>
          <WikiTextEditor
            v-if="isEditorShown(index)"
            format="markdown"
            :wiki-text="objectiveTexts[index]"
            :label="`${objectiveContentTitle(objective)} statement`"
            :strategies="strategies"
            :read-only="isReadOnly"
            :read-only-hint="readOnlyHint"
            @update:wiki-text="editObjective(index, $event)"
          />
          <WikiText v-if="isReadOnlyTextShown(objective)" :text="objective.text" :strategies="strategies" :locked="accessInfo.blur" />
        </template>
        <template #objective>
          <WikiText :text="displayedText(objective, index)" :strategies="strategies" popover-placement="bottom" :locked="accessInfo.blur" />
        </template>
        <template v-if="hasResourceSection(objective.level)" #footer>
          <div data-testid="objective-resources">
            <p v-if="isFetchingResources" class="my-2.5 flex items-center text-text-md text-gray-700" data-testid="objective-resources-loading">
              <ProgressSpinner stroke-width="8" class="!ml-0 !mr-2 h-4 w-4 stroke-gray-700" aria-hidden="true" />
              Loading Resources...
            </p>
            <template v-if="hasAssessmentsSection(objective.level)">
              <h4 :class="[SECTION_HEADING_CLASSES, levelSectionHeadingClass(objective.level)]">Assessments</h4>
              <ResourceCard
                v-for="resource in curatedAssessments(objective.level)"
                :key="resource.uuid"
                v-bind="resource"
                :heading-level="5"
              />
              <div v-if="hasGeneratedAssessments(objective.level)" class="flex flex-wrap">
                <slot name="generatedAssessments" :objective="objective" :index="index" />
              </div>
            </template>
            <template v-if="readyToUseResources(objective.level).length > 0">
              <h4 :class="[SECTION_HEADING_CLASSES, levelSectionHeadingClass(objective.level)]">Ready-To-Use Resources</h4>
              <ResourceCard
                v-for="resource in readyToUseResources(objective.level)"
                :key="resource.uuid"
                v-bind="resource"
                :heading-level="5"
              />
            </template>
          </div>
        </template>
      </Objective>
    </TabsContent>
  </TabsRoot>
</template>
